import FamilyRestroomRoundedIcon from '@mui/icons-material/FamilyRestroomRounded'
import LanRoundedIcon from '@mui/icons-material/LanRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded'
import TextSnippetRoundedIcon from '@mui/icons-material/TextSnippetRounded'
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded'
import WorkRoundedIcon from '@mui/icons-material/WorkRounded'
import { Grid, Stack, Tab } from '@mui/material'
import Breadcrumbs from 'components/Breadcrumbs'
import Label from 'components/Label'
import { FormTabsProps, useTab } from 'hooks/tab.hook'
import Layout from 'layouts/Default'
import { TabEducation } from 'pages/TeenagersRecord/components/TabEducation'
import { TabFamily } from 'pages/TeenagersRecord/components/TabFamily'
import { TabHealth } from 'pages/TeenagersRecord/components/TabHealth'
import { TabRelatedProcesses } from 'pages/TeenagersRecord/components/TabRelatedProcesses'
import { TabWelcome } from 'pages/TeenagersRecord/components/TabWelcome'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import teenagerService from 'services/teenager.service'
import { HealthIcon } from '../../assets/icons/HealthIcon'
import TabContent from './components/Record/components/TabContent'
import { UserInfo } from './components/Record/components/UserInfo'
import { TabFamiliarReference } from './components/TabFamiliarReference'
import { TabPersonalData } from './components/TabPersonalData'
import { TabProfessionalData } from './components/TabProfessionalData'
import { StyledBoxContainer, StyledTabs as Tabs, StyledTabsContent as TabsContent } from './styles'
import { TeenagerType } from 'types/teenager-record.type'
import UploadPicture from 'components/UploadPicture'
import { useTeenagerPictures } from 'hooks/teenager-pictures.hook'
import { obterTamanhoArquivo } from 'utils/base64.utils'

export type TabTeenagerProps = {
  data?: TeenagerType
}

export const TeenagersRecordPage: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [data, setData] = useState<TeenagerType | undefined>(undefined)

  const tabs: FormTabsProps[] = [
    {
      index: 0,
      label: 'Dados pessoais',
      icon: <PersonRoundedIcon color='primary' />,
      content: <TabPersonalData data={data} />,
    },
    {
      index: 1,
      label: 'Processos relacionados',
      icon: <TextSnippetRoundedIcon color={'primary'} />,
      content: <TabRelatedProcesses />,
    },
    {
      index: 2,
      label: 'Saúde',
      icon: <HealthIcon color='primary' />,
      content: <TabHealth data={data} />,
    },
    {
      index: 3,
      label: 'Educação',
      icon: <SchoolRoundedIcon color='primary' />,
      content: <TabEducation data={data} />,
    },
    {
      index: 4,
      label: 'Dados profissionais',
      icon: <WorkRoundedIcon color='primary' />,
      content: <TabProfessionalData data={data} />,
    },
    {
      index: 5,
      label: 'Família',
      icon: <FamilyRestroomRoundedIcon color='primary' />,
      content: <TabFamily data={data} />,
    },
    {
      index: 6,
      label: 'Acolhimento',
      icon: <VolunteerActivismRoundedIcon color='primary' />,
      content: <TabWelcome data={data} />,
    },
    {
      index: 7,
      label: 'Referência familiar',
      icon: <LanRoundedIcon color='primary' />,
      content: <TabFamiliarReference />,
    },
  ]

  const {
    tab: { current },
    handleChange,
  } = useTab(tabs)
  const { image, handleChangePicture, handleResetPicture } = useTeenagerPictures(id)
  const fillTeenagerData = useCallback(async (): Promise<void> => {
    try {
      if (!id) return
      const [teenagerDataRes] = await Promise.all([
        teenagerService.getById(parseInt(id)).then((res) => res.data),
        // teenagerService.getPicture(parseInt(id)).then((res) => res.data),
      ])

      setData(teenagerDataRes)
      // setTeenagerPicture(teenagerPictureRes)
    } catch (err) {
      console.error('Teenager Info Requests: ', err)
    }
  }, [id])

  const handleBackTeenagerRecords = (): void => navigate(`/consultar-adolescente`)

  useEffect(() => {
    fillTeenagerData()
  }, [fillTeenagerData, image])

  return (
    <Layout>
      <Grid container spacing={1}>
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Breadcrumbs>
            <div onClick={handleBackTeenagerRecords} style={{ cursor: 'pointer' }}>
              <Label text='Consultar Adolescente' />
            </div>
            <Label text='Ficha Adolescente' bold />
          </Breadcrumbs>
        </Grid>
        <Grid item xs={12}>
          {data && (
            <>
              <Grid container spacing={2}>
                <Grid item lg={3} md={4} xs={12}>
                  <StyledBoxContainer>
                    <Stack direction='column' spacing={2} sx={{ textAlign: 'center' }}>
                      <UploadPicture
                        handleChange={handleChangePicture}
                        handleReset={handleResetPicture}
                        image={image}
                        uploadMode={false}
                        metadados={{
                          nomeArquivo: data.identAdolescente.foto?.arquivoNome,
                          tipoArquivo: data.identAdolescente.foto?.arquivoTipo,
                          tamanho: obterTamanhoArquivo(data.identAdolescente.foto?.arquivoBase64),
                        }}
                      />
                      <UserInfo data={data} />
                    </Stack>
                  </StyledBoxContainer>
                </Grid>
                <Grid item lg={9} md={8} xs={12}>
                  <Stack direction='column' spacing={2}>
                    <StyledBoxContainer>
                      <Tabs value={current} onChange={handleChange}>
                        {tabs.map((item) => (
                          <Tab
                            key={item.index}
                            value={item.index}
                            label={item.label}
                            icon={item.icon}
                            iconPosition='start'
                          />
                        ))}
                      </Tabs>
                    </StyledBoxContainer>
                    <TabsContent>
                      {tabs.map((item) => (
                        <TabContent key={item.index} value={current} index={item.index}>
                          {item.content}
                        </TabContent>
                      ))}
                    </TabsContent>
                  </Stack>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </Layout>
  )
}
